import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../lib/utils";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            "fixed inset-0 z-50 bg-black/60",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            className
        )}
        {...props}
    />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef(
    ({ className, children, size = "md", ...props }, ref) => {
        const maxWidths = {
            sm: 384,
            md: 512,
            lg: 672,
            xl: 896,
        };
        const maxW = maxWidths[size] || maxWidths.md;

        return (
            <DialogPortal>
                <DialogOverlay />
                <div className="fixed inset-0 z-50 flex items-start justify-center sm:items-center p-0 sm:p-4 pointer-events-none">
                    <DialogPrimitive.Content
                        ref={ref}
                        style={{ maxWidth: maxW }}
                        className={cn(
                            "pointer-events-auto w-full bg-white shadow-xl",
                            "max-h-[90dvh] overflow-y-auto",
                            "rounded-b-xl sm:rounded-xl",
                            "data-[state=open]:animate-in data-[state=closed]:animate-out",
                            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                            className
                        )}
                        {...props}
                    >
                        {children}
                    </DialogPrimitive.Content>
                </div>
            </DialogPortal>
        );
    }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

function DialogHeader({ className, ...props }) {
    return (
        <div
            className={cn("flex flex-col space-y-1.5 px-6 pt-6 pb-0", className)}
            {...props}
        />
    );
}
DialogHeader.displayName = "DialogHeader";

function DialogFooter({ className, ...props }) {
    return (
        <div
            className={cn(
                "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 px-6 pb-6 pt-0",
                className
            )}
            {...props}
        />
    );
}
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn("text-xl font-semibold leading-none tracking-tight", className)}
        {...props}
    />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

function DialogBody({ className, ...props }) {
    return <div className={cn("px-6 py-4", className)} {...props} />;
}
DialogBody.displayName = "DialogBody";

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn("text-sm text-muted-foreground", className)}
        {...props}
    />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const XIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
);

function DialogCloseButton({ className, ...props }) {
    return (
        <DialogClose
            className={cn(
                "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none",
                className
            )}
            {...props}
        >
            <XIcon />
            <span className="sr-only">Zavřít</span>
        </DialogClose>
    );
}

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
    DialogBody,
    DialogCloseButton,
};
